import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Button, CarouselButtonNext } from '~/components-v2'
import clsx from 'clsx'

import { carouselHeroData } from '../data/carousel-hero'

interface CarouselHeroProps {
  data?: typeof carouselHeroData
  heading?: ReactNode
  subheading?: ReactNode
  cta?: ReactNode
}

const defaultCta = <Button to="/request-demo">Get a Demo</Button>

export const CarouselHero = ({
  data = carouselHeroData,
  heading = 'Ship AI analytics without slowing down your roadmap',
  subheading = 'White label Omni in your product with SSO embedding, APIs, and our MCP server.',
  cta = defaultCta,
}: CarouselHeroProps) => {
  const [index, setIndex] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { images } = data

  const startAutoToggle = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    intervalRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length)
    }, 5000)
  }, [images.length])

  useEffect(() => {
    startAutoToggle()

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [startAutoToggle])

  const showPreviousSlide = () => {
    setIndex((prev) => (prev - 1 + images.length) % images.length)
    startAutoToggle()
  }

  const showNextSlide = () => {
    setIndex((prev) => (prev + 1) % images.length)
    startAutoToggle()
  }

  return (
    <section className="page-section page-section--lg section-spacing carousel-pipeline">
      <div className="page-section__inner">
        <hgroup className="carousel-pipeline-copy">
          <h2 className="heading-xl text-brand-dark">{heading}</h2>
          <p className="body-l text-primary">{subheading}</p>

          {cta}
        </hgroup>

        <div className="carousel-pipeline-media">
          <div className="carousel-hero">
            {images.map((image, i) => {
              const { alt, src } = image

              return (
                <img
                  key={src}
                  src={src}
                  alt={alt}
                  className={clsx('carousel-image', i === index && 'active')}
                />
              )
            })}
          </div>

          <div className="carousel-controls-next carousel-pipeline-controls">
            <CarouselButtonNext
              aria-label="Show previous dashboard"
              direction="previous"
              onClick={showPreviousSlide}
            />
            <CarouselButtonNext
              aria-label="Show next dashboard"
              direction="next"
              onClick={showNextSlide}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
